import React from 'react';
import SEO from '../components/SEO';
import '../styles.css';

function TermsPage() {
  return (
    <div className="page-container">
      <SEO
        title="Virgil Terms of Service"
        description="Terms governing Virgil accounts, the public-beta trial, subscriptions, AI features, and desktop software."
        url="https://www.virgil.com.ai/terms"
      />
      <section className="content-section">
        <h1 className="page-title">Terms of Service</h1>
        <div className="page-content legal-content">
          <p className="page-meta">Last updated: August 17, 2026</p>

          <h2>Agreement</h2>
          <p>
            These Terms govern your use of Virgil&apos;s website, desktop
            software, AI writing tools, and related services. By creating an
            account or using Virgil, you agree to these Terms. If you do not
            agree, do not use the service.
          </p>

          <h2>Accounts and public-beta access</h2>
          <p>
            You must provide accurate account information, keep your login
            secure, and promptly report unauthorized access. Public-beta access
            is limited to 100 unique activated accounts. A seat is claimed when
            Stripe successfully starts a trial and cannot be transferred or used
            to obtain another trial. Reused accounts, verified email addresses,
            or payment methods may be declined to enforce one trial per person;
            contact support if a legitimate household shares a payment method.
          </p>

          <h2>14-day trial and automatic renewal</h2>
          <ul>
            <li>A valid payment card is required to start the trial.</li>
            <li>
              Trial AI access lasts for 14 days or $20 of included AI usage,
              whichever comes first. AI usage stops at the $20 limit; there is
              no trial overage.
            </li>
            <li>
              Reaching the usage limit does not trigger an early charge. Unless
              you cancel, your selected Starter, Pro, or Studio plan begins and
              is charged when Stripe&apos;s 14-day trial period ends.
            </li>
            <li>
              The paid plan then receives its normal monthly AI allowance.
              Usage above that paid allowance may be metered and charged at the
              rates disclosed at checkout.
            </li>
          </ul>
          <p>
            Stripe controls payment collection, trial timing, subscription
            status, renewal, and receipts. The price and plan shown in Stripe
            Checkout are the final billing terms for that purchase.
          </p>

          <h2>Cancellation and refunds</h2>
          <p>
            You may cancel before the trial ends to avoid the first monthly
            charge. Cancellation may end app and download access immediately.
            Except where required by law, subscription and usage charges are
            non-refundable. If you believe a charge was made in error, contact{' '}
            <a href="mailto:support@virgil.com.ai">support@virgil.com.ai</a>{' '}
            within seven days so we can review it.
          </p>

          <h2>Your work and AI output</h2>
          <p>
            You keep your rights in content you submit and work you create.
            You give Virgil the limited permission needed to store, transmit,
            and process that content to operate the service. AI output can be
            inaccurate, incomplete, offensive, or similar to material produced
            for others. You are responsible for reviewing output and deciding
            whether you have the rights and permissions needed to use or publish
            it.
          </p>

          <h2>AI providers and third-party services</h2>
          <p>
            Virgil may send your prompts and relevant project context to the AI
            provider you select. Auth0 provides authentication, Stripe provides
            billing, and AWS and other infrastructure providers support storage
            and delivery. Their services may be governed by additional terms.
          </p>

          <h2>Desktop software and updates</h2>
          <p>
            Beta builds may contain errors, change without notice, or be
            unavailable on some devices. You are responsible for maintaining a
            supported operating system and backups of important work. Virgil may
            provide required security, compatibility, or feature updates; older
            builds may stop connecting to the service.
          </p>

          <h2>Acceptable use</h2>
          <p>
            Do not use Virgil to violate law or another person&apos;s rights,
            distribute malware, bypass usage or access controls, interfere with
            the service, scrape or resell access, or attempt to obtain another
            person&apos;s data. We may suspend access needed to protect users,
            providers, or the service.
          </p>

          <h2>Beta limitations and warranties</h2>
          <p>
            The public beta is provided &quot;as is&quot; and &quot;as
            available.&quot; Features may change, data may be lost, and service
            may be interrupted. To the extent permitted by law, Virgil disclaims
            implied warranties and is not liable for indirect, incidental,
            special, consequential, or lost-profit damages.
          </p>

          <h2>Changes and termination</h2>
          <p>
            We may update these Terms as the beta changes. We will post the
            revised date and provide additional notice when legally required.
            You may stop using Virgil at any time. Provisions that by their
            nature should survive termination will continue to apply.
          </p>

          <h2>Contact</h2>
          <p>
            Questions about these Terms, billing, cancellation, or refunds:
            {' '}<a href="mailto:support@virgil.com.ai">support@virgil.com.ai</a>.
          </p>
        </div>
      </section>
    </div>
  );
}

export default TermsPage;
